import type { ReportEntity } from './types';
import { EXTRA_REPORT_TEMPLATES } from './reportTemplatesExtra';
import { DASHBOARD_TEMPLATES } from './reportDashboardTemplates';

export type ReportTemplateCategory =
  | 'Analytics'
  | 'ChatBot'
  | 'Operations'
  | 'Finance'
  | 'Compliance'
  | 'Sales';

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: ReportTemplateCategory;
  icon: string;
  gradient: string;
  suggestedName: string;
  htmlTemplate: string;
  entities: Omit<ReportEntity, 'id'>[];
  variant?: 'dashboard' | 'report';
}

const BASE_FONT = `'Inter', 'Segoe UI', system-ui, sans-serif`;

export const REPORT_TEMPLATE_CATEGORIES: ReportTemplateCategory[] = [
  'Analytics',
  'ChatBot',
  'Operations',
  'Finance',
  'Compliance',
  'Sales',
];

const CORE_REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'executive-dashboard',
    name: 'Executive Dashboard',
    description: 'High-level KPI overview with hero metrics, trend indicators, and executive summary.',
    category: 'Analytics',
    icon: 'BarChart3',
    gradient: 'from-indigo-600 to-violet-700',
    suggestedName: 'Executive Summary Report',
    entities: [
      {
        name: 'summary',
        color: 'indigo',
        data: {
          title: 'Executive Performance Summary',
          period: 'Q2 2025',
          generatedAt: 'June 20, 2025',
          kpis: {
            revenue: '$2.4M',
            growth: '+18.2%',
            customers: '12,480',
            nps: '72',
          },
          highlights: [
            'Revenue exceeded target by 12%',
            'Customer retention improved to 94%',
            'New market expansion launched in APAC',
          ],
        },
      },
      {
        name: 'meta',
        color: 'violet',
        data: { department: 'Executive Office', owner: 'Strategy Team', confidentiality: 'Internal' },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: ${BASE_FONT}; background: #f8fafc; color: #0f172a; padding: 32px; }
  .hero { background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%); color: #fff; border-radius: 16px; padding: 28px 32px; margin-bottom: 24px; }
  .hero h1 { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; }
  .hero p { opacity: 0.85; font-size: 13px; margin-top: 6px; }
  .badge { display: inline-block; background: rgba(255,255,255,0.2); padding: 4px 10px; border-radius: 999px; font-size: 11px; margin-top: 12px; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
  .kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; }
  .kpi-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; font-weight: 600; }
  .kpi-value { font-size: 28px; font-weight: 800; color: #312e81; margin-top: 4px; letter-spacing: -0.02em; }
  .section { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; }
  .section h2 { font-size: 14px; font-weight: 700; color: #334155; margin-bottom: 12px; }
  .highlights li { font-size: 13px; color: #475569; padding: 8px 0; border-bottom: 1px solid #f1f5f9; list-style: none; padding-left: 20px; position: relative; }
  .highlights li:before { content: "✓"; position: absolute; left: 0; color: #6366f1; font-weight: 700; }
  .footer { margin-top: 24px; font-size: 11px; color: #94a3b8; text-align: center; }
</style></head><body>
  <div class="hero">
    <h1>{{summary.title}}</h1>
    <p>{{summary.period}} · Generated {{summary.generatedAt}}</p>
    <span class="badge">{{meta.confidentiality}}</span>
  </div>
  <div class="grid">
    <div class="kpi"><div class="kpi-label">Revenue</div><div class="kpi-value">{{summary.kpis.revenue}}</div></div>
    <div class="kpi"><div class="kpi-label">Growth</div><div class="kpi-value">{{summary.kpis.growth}}</div></div>
    <div class="kpi"><div class="kpi-label">Customers</div><div class="kpi-value">{{summary.kpis.customers}}</div></div>
    <div class="kpi"><div class="kpi-label">NPS Score</div><div class="kpi-value">{{summary.kpis.nps}}</div></div>
  </div>
  <div class="section">
    <h2>Key Highlights</h2>
    <ul class="highlights">
      <li>{{summary.highlights.0}}</li>
      <li>{{summary.highlights.1}}</li>
      <li>{{summary.highlights.2}}</li>
    </ul>
  </div>
  <div class="footer">{{meta.department}} · {{meta.owner}}</div>
</body></html>`,
  },
  {
    id: 'conversation-analytics',
    name: 'Conversation Analytics',
    description: 'ChatBot session metrics, resolution rates, sentiment, and channel breakdown.',
    category: 'ChatBot',
    icon: 'MessageSquare',
    gradient: 'from-cyan-500 to-blue-600',
    suggestedName: 'Conversation Analytics Report',
    entities: [
      {
        name: 'conversations',
        color: 'cyan',
        data: {
          title: 'Conversation Analytics',
          period: 'Last 30 days',
          totalSessions: 8420,
          avgDuration: '4m 18s',
          resolutionRate: '87%',
          escalationRate: '4.2%',
          sentiment: { positive: '72%', neutral: '21%', negative: '7%' },
        },
      },
      {
        name: 'agent',
        color: 'indigo',
        data: { name: 'Support Assistant', version: '3.2', environment: 'Production' },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #fff; color: #1e293b; }
  .header { border-bottom: 3px solid #06b6d4; padding-bottom: 16px; margin-bottom: 24px; }
  h1 { font-size: 22px; color: #0e7490; }
  .sub { color: #64748b; font-size: 12px; margin-top: 4px; }
  .metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
  .card { background: linear-gradient(180deg, #ecfeff, #f0fdfa); border: 1px solid #a5f3fc; border-radius: 10px; padding: 16px; text-align: center; }
  .card-label { font-size: 10px; text-transform: uppercase; color: #0891b2; font-weight: 600; }
  .card-value { font-size: 24px; font-weight: 800; color: #164e63; margin-top: 4px; }
  .sentiment { display: flex; gap: 12px; margin-top: 16px; }
  .sent { flex: 1; padding: 14px; border-radius: 8px; text-align: center; font-size: 13px; font-weight: 600; }
  .pos { background: #dcfce7; color: #166534; }
  .neu { background: #f1f5f9; color: #475569; }
  .neg { background: #fee2e2; color: #991b1b; }
  .footer { margin-top: 28px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 12px; }
</style></head><body>
  <div class="header">
    <h1>{{conversations.title}}</h1>
    <p class="sub">{{conversations.period}} · Agent: {{agent.name}} v{{agent.version}}</p>
  </div>
  <div class="metrics">
    <div class="card"><div class="card-label">Sessions</div><div class="card-value">{{conversations.totalSessions}}</div></div>
    <div class="card"><div class="card-label">Avg Duration</div><div class="card-value">{{conversations.avgDuration}}</div></div>
    <div class="card"><div class="card-label">Resolution</div><div class="card-value">{{conversations.resolutionRate}}</div></div>
    <div class="card"><div class="card-label">Escalation</div><div class="card-value">{{conversations.escalationRate}}</div></div>
  </div>
  <h3 style="font-size:13px;color:#334155;margin-bottom:8px;">Sentiment Distribution</h3>
  <div class="sentiment">
    <div class="sent pos">Positive {{conversations.sentiment.positive}}</div>
    <div class="sent neu">Neutral {{conversations.sentiment.neutral}}</div>
    <div class="sent neg">Negative {{conversations.sentiment.negative}}</div>
  </div>
  <div class="footer">{{agent.environment}} environment · Generated automatically</div>
</body></html>`,
  },
  {
    id: 'intent-performance',
    name: 'Intent Performance',
    description: 'NLU accuracy, top intents table, fallback analysis, and training recommendations.',
    category: 'ChatBot',
    icon: 'PieChart',
    gradient: 'from-violet-500 to-purple-700',
    suggestedName: 'Intent Performance Report',
    entities: [
      {
        name: 'intents',
        color: 'violet',
        data: {
          title: 'Intent Recognition Report',
          period: 'Last 7 days',
          overallAccuracy: '91.4%',
          fallbackRate: '3.8%',
          topIntents: [
            { name: 'Order Status', count: 2840, accuracy: '94%' },
            { name: 'Returns', count: 1920, accuracy: '89%' },
            { name: 'Product Info', count: 1560, accuracy: '92%' },
          ],
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #faf5ff; color: #1e1b4b; }
  h1 { font-size: 20px; color: #6d28d9; }
  .meta { font-size: 12px; color: #7c3aed; margin: 6px 0 20px; }
  .stats { display: flex; gap: 16px; margin-bottom: 24px; }
  .stat { background: #fff; border: 1px solid #ddd6fe; border-radius: 10px; padding: 16px 24px; flex: 1; }
  .stat-label { font-size: 10px; text-transform: uppercase; color: #8b5cf6; }
  .stat-val { font-size: 26px; font-weight: 800; color: #5b21b6; }
  table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
  th { background: #7c3aed; color: #fff; text-align: left; padding: 10px 14px; font-size: 11px; text-transform: uppercase; }
  td { padding: 12px 14px; border-bottom: 1px solid #ede9fe; font-size: 13px; }
  tr:last-child td { border-bottom: none; }
  .acc { color: #059669; font-weight: 600; }
</style></head><body>
  <h1>{{intents.title}}</h1>
  <p class="meta">{{intents.period}}</p>
  <div class="stats">
    <div class="stat"><div class="stat-label">Overall Accuracy</div><div class="stat-val">{{intents.overallAccuracy}}</div></div>
    <div class="stat"><div class="stat-label">Fallback Rate</div><div class="stat-val">{{intents.fallbackRate}}</div></div>
  </div>
  <table>
    <thead><tr><th>Intent</th><th>Triggers</th><th>Accuracy</th></tr></thead>
    <tbody>
      <tr><td>{{intents.topIntents.0.name}}</td><td>{{intents.topIntents.0.count}}</td><td class="acc">{{intents.topIntents.0.accuracy}}</td></tr>
      <tr><td>{{intents.topIntents.1.name}}</td><td>{{intents.topIntents.1.count}}</td><td class="acc">{{intents.topIntents.1.accuracy}}</td></tr>
      <tr><td>{{intents.topIntents.2.name}}</td><td>{{intents.topIntents.2.count}}</td><td class="acc">{{intents.topIntents.2.accuracy}}</td></tr>
    </tbody>
  </table>
</body></html>`,
  },
  {
    id: 'operations-sla',
    name: 'Operations & SLA',
    description: 'Uptime, latency percentiles, incident counts, and SLA compliance tracking.',
    category: 'Operations',
    icon: 'Activity',
    gradient: 'from-emerald-500 to-teal-600',
    suggestedName: 'Operations SLA Report',
    entities: [
      {
        name: 'ops',
        color: 'emerald',
        data: {
          title: 'Operations & SLA Report',
          period: 'June 2025',
          uptime: '99.97%',
          p50Latency: '142ms',
          p99Latency: '890ms',
          incidents: 3,
          slaCompliance: '100%',
          targets: { uptime: '99.9%', p99: '< 1000ms' },
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #ecfdf5; color: #064e3b; }
  .banner { background: #059669; color: #fff; padding: 20px 24px; border-radius: 12px; margin-bottom: 20px; }
  .banner h1 { font-size: 20px; }
  .banner span { font-size: 12px; opacity: 0.9; }
  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .box { background: #fff; border: 1px solid #a7f3d0; border-radius: 10px; padding: 18px; }
  .box-label { font-size: 10px; text-transform: uppercase; color: #059669; font-weight: 600; }
  .box-value { font-size: 26px; font-weight: 800; margin-top: 4px; }
  .sla-ok { color: #059669; }
  .sla-box { grid-column: span 3; background: linear-gradient(90deg, #d1fae5, #ecfdf5); margin-top: 14px; display: flex; justify-content: space-between; align-items: center; }
  .target { font-size: 12px; color: #047857; }
</style></head><body>
  <div class="banner"><h1>{{ops.title}}</h1><span>{{ops.period}}</span></div>
  <div class="grid">
    <div class="box"><div class="box-label">Uptime</div><div class="box-value sla-ok">{{ops.uptime}}</div></div>
    <div class="box"><div class="box-label">P50 Latency</div><div class="box-value">{{ops.p50Latency}}</div></div>
    <div class="box"><div class="box-label">P99 Latency</div><div class="box-value">{{ops.p99Latency}}</div></div>
    <div class="box"><div class="box-label">Incidents</div><div class="box-value">{{ops.incidents}}</div></div>
    <div class="box sla-box">
      <div><div class="box-label">SLA Compliance</div><div class="box-value sla-ok">{{ops.slaCompliance}}</div></div>
      <div class="target">Target: {{ops.targets.uptime}} uptime · P99 {{ops.targets.p99}}</div>
    </div>
  </div>
</body></html>`,
  },
  {
    id: 'financial-summary',
    name: 'Financial Summary',
    description: 'Revenue, expenses, profit margins, and period-over-period comparison.',
    category: 'Finance',
    icon: 'TrendingUp',
    gradient: 'from-amber-500 to-orange-600',
    suggestedName: 'Financial Summary Report',
    entities: [
      {
        name: 'finance',
        color: 'amber',
        data: {
          title: 'Financial Summary',
          period: 'FY 2025 — H1',
          currency: 'USD',
          revenue: '$4,820,000',
          expenses: '$3,140,000',
          netProfit: '$1,680,000',
          margin: '34.9%',
          yoyGrowth: '+22.4%',
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #fffbeb; color: #78350f; }
  h1 { font-size: 22px; color: #b45309; border-bottom: 2px solid #fbbf24; padding-bottom: 10px; }
  .period { font-size: 12px; color: #d97706; margin: 8px 0 24px; }
  .row { display: flex; gap: 16px; margin-bottom: 16px; }
  .fin-card { flex: 1; background: #fff; border: 1px solid #fde68a; border-radius: 10px; padding: 20px; }
  .fin-card.highlight { background: linear-gradient(135deg, #fef3c7, #fff); border-color: #f59e0b; }
  .label { font-size: 10px; text-transform: uppercase; color: #92400e; font-weight: 600; }
  .amount { font-size: 28px; font-weight: 800; color: #78350f; margin-top: 4px; }
  .growth { display: inline-block; background: #dcfce7; color: #166534; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 700; margin-top: 12px; }
</style></head><body>
  <h1>{{finance.title}}</h1>
  <p class="period">{{finance.period}} · {{finance.currency}}</p>
  <div class="row">
    <div class="fin-card"><div class="label">Total Revenue</div><div class="amount">{{finance.revenue}}</div></div>
    <div class="fin-card"><div class="label">Total Expenses</div><div class="amount">{{finance.expenses}}</div></div>
  </div>
  <div class="row">
    <div class="fin-card highlight"><div class="label">Net Profit</div><div class="amount">{{finance.netProfit}}</div><span class="growth">YoY {{finance.yoyGrowth}}</span></div>
    <div class="fin-card"><div class="label">Profit Margin</div><div class="amount">{{finance.margin}}</div></div>
  </div>
</body></html>`,
  },
  {
    id: 'compliance-audit',
    name: 'Compliance Audit',
    description: 'Formal audit layout with control checks, findings, and sign-off section.',
    category: 'Compliance',
    icon: 'ClipboardList',
    gradient: 'from-slate-600 to-slate-800',
    suggestedName: 'Compliance Audit Report',
    entities: [
      {
        name: 'audit',
        color: 'violet',
        data: {
          title: 'Compliance Audit Report',
          standard: 'SOC 2 Type II',
          period: 'Audit Period: Jan–Jun 2025',
          status: 'Compliant',
          controlsPassed: 47,
          controlsFailed: 2,
          findings: [
            'Access review completed for all privileged accounts',
            'Minor finding: MFA enrollment at 94% (target 100%)',
          ],
          auditor: 'Internal Audit Team',
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: 'Georgia', serif; padding: 36px; background: #fff; color: #1e293b; max-width: 800px; margin: 0 auto; }
  .letterhead { border-bottom: 3px double #334155; padding-bottom: 16px; margin-bottom: 24px; }
  h1 { font-size: 22px; font-weight: 400; letter-spacing: 0.02em; }
  .std { font-size: 13px; color: #64748b; margin-top: 4px; }
  .status { display: inline-block; background: #dcfce7; color: #166534; padding: 6px 16px; border-radius: 4px; font-family: ${BASE_FONT}; font-size: 12px; font-weight: 700; margin: 16px 0; }
  .counts { display: flex; gap: 24px; font-family: ${BASE_FONT}; margin: 20px 0; }
  .count { font-size: 13px; } .count strong { font-size: 24px; display: block; color: #334155; }
  .findings { font-family: ${BASE_FONT}; }
  .findings h2 { font-size: 14px; margin-bottom: 10px; }
  .findings li { font-size: 13px; padding: 8px 0; border-bottom: 1px solid #e2e8f0; list-style: decimal; margin-left: 20px; }
  .sign { margin-top: 40px; border-top: 1px solid #cbd5e1; padding-top: 20px; font-size: 12px; color: #64748b; }
</style></head><body>
  <div class="letterhead">
    <h1>{{audit.title}}</h1>
    <p class="std">{{audit.standard}} · {{audit.period}}</p>
  </div>
  <div class="status">{{audit.status}}</div>
  <div class="counts">
    <div class="count"><strong>{{audit.controlsPassed}}</strong>Controls Passed</div>
    <div class="count"><strong>{{audit.controlsFailed}}</strong>Findings</div>
  </div>
  <div class="findings">
    <h2>Audit Findings</h2>
    <ol>
      <li>{{audit.findings.0}}</li>
      <li>{{audit.findings.1}}</li>
    </ol>
  </div>
  <div class="sign">Prepared by {{audit.auditor}}</div>
</body></html>`,
  },
  {
    id: 'sales-pipeline',
    name: 'Sales Pipeline',
    description: 'Funnel stages, deal values, conversion rates, and forecast summary.',
    category: 'Sales',
    icon: 'Users',
    gradient: 'from-rose-500 to-pink-600',
    suggestedName: 'Sales Pipeline Report',
    entities: [
      {
        name: 'pipeline',
        color: 'rose',
        data: {
          title: 'Sales Pipeline Report',
          period: 'Q2 2025',
          totalValue: '$3.2M',
          forecast: '$1.1M',
          winRate: '28%',
          stages: [
            { name: 'Prospect', deals: 84, value: '$980K' },
            { name: 'Qualified', deals: 42, value: '$720K' },
            { name: 'Proposal', deals: 18, value: '$540K' },
            { name: 'Closed Won', deals: 12, value: '$380K' },
          ],
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #fff1f2; color: #881337; }
  h1 { font-size: 20px; color: #be123c; }
  .sub { font-size: 12px; color: #e11d48; margin-bottom: 20px; }
  .top { display: flex; gap: 14px; margin-bottom: 24px; }
  .pill { flex: 1; background: #fff; border-radius: 10px; padding: 16px; border: 1px solid #fecdd3; text-align: center; }
  .pill-label { font-size: 10px; text-transform: uppercase; color: #e11d48; }
  .pill-val { font-size: 22px; font-weight: 800; color: #9f1239; margin-top: 4px; }
  .funnel { display: flex; flex-direction: column; gap: 8px; }
  .stage { display: flex; align-items: center; background: #fff; border-radius: 8px; padding: 12px 16px; border-left: 4px solid #f43f5e; }
  .stage-name { flex: 1; font-weight: 600; font-size: 13px; }
  .stage-deals { font-size: 12px; color: #64748b; margin-right: 16px; }
  .stage-val { font-weight: 700; color: #be123c; font-size: 14px; }
</style></head><body>
  <h1>{{pipeline.title}}</h1>
  <p class="sub">{{pipeline.period}}</p>
  <div class="top">
    <div class="pill"><div class="pill-label">Pipeline Value</div><div class="pill-val">{{pipeline.totalValue}}</div></div>
    <div class="pill"><div class="pill-label">Forecast</div><div class="pill-val">{{pipeline.forecast}}</div></div>
    <div class="pill"><div class="pill-label">Win Rate</div><div class="pill-val">{{pipeline.winRate}}</div></div>
  </div>
  <div class="funnel">
    <div class="stage"><span class="stage-name">{{pipeline.stages.0.name}}</span><span class="stage-deals">{{pipeline.stages.0.deals}} deals</span><span class="stage-val">{{pipeline.stages.0.value}}</span></div>
    <div class="stage"><span class="stage-name">{{pipeline.stages.1.name}}</span><span class="stage-deals">{{pipeline.stages.1.deals}} deals</span><span class="stage-val">{{pipeline.stages.1.value}}</span></div>
    <div class="stage"><span class="stage-name">{{pipeline.stages.2.name}}</span><span class="stage-deals">{{pipeline.stages.2.deals}} deals</span><span class="stage-val">{{pipeline.stages.2.value}}</span></div>
    <div class="stage"><span class="stage-name">{{pipeline.stages.3.name}}</span><span class="stage-deals">{{pipeline.stages.3.deals}} deals</span><span class="stage-val">{{pipeline.stages.3.value}}</span></div>
  </div>
</body></html>`,
  },
  {
    id: 'workflow-execution',
    name: 'Workflow Execution',
    description: 'Run statistics, success/failure rates, duration metrics, and error summary.',
    category: 'Operations',
    icon: 'Workflow',
    gradient: 'from-blue-500 to-indigo-600',
    suggestedName: 'Workflow Execution Report',
    entities: [
      {
        name: 'workflow',
        color: 'indigo',
        data: {
          title: 'Workflow Execution Report',
          workflowName: 'Customer Onboarding Flow',
          period: 'Last 14 days',
          totalRuns: 1840,
          successful: 1762,
          failed: 78,
          successRate: '95.8%',
          avgDuration: '2m 14s',
          lastRun: '2025-06-20 09:42 UTC',
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #eff6ff; color: #1e3a8a; }
  .header { background: #2563eb; color: #fff; padding: 20px 24px; border-radius: 12px; margin-bottom: 20px; }
  h1 { font-size: 18px; }
  .wf-name { font-size: 13px; opacity: 0.9; margin-top: 4px; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .cell { background: #fff; border-radius: 8px; padding: 16px; border: 1px solid #bfdbfe; }
  .cell-label { font-size: 10px; text-transform: uppercase; color: #3b82f6; font-weight: 600; }
  .cell-val { font-size: 22px; font-weight: 800; margin-top: 4px; }
  .success { color: #059669; } .fail { color: #dc2626; }
  .last { margin-top: 20px; font-size: 11px; color: #64748b; }
</style></head><body>
  <div class="header">
    <h1>{{workflow.title}}</h1>
    <p class="wf-name">{{workflow.workflowName}} · {{workflow.period}}</p>
  </div>
  <div class="grid">
    <div class="cell"><div class="cell-label">Total Runs</div><div class="cell-val">{{workflow.totalRuns}}</div></div>
    <div class="cell"><div class="cell-label">Successful</div><div class="cell-val success">{{workflow.successful}}</div></div>
    <div class="cell"><div class="cell-label">Failed</div><div class="cell-val fail">{{workflow.failed}}</div></div>
    <div class="cell"><div class="cell-label">Success Rate</div><div class="cell-val success">{{workflow.successRate}}</div></div>
  </div>
  <p class="last">Avg duration: {{workflow.avgDuration}} · Last run: {{workflow.lastRun}}</p>
</body></html>`,
  },
  {
    id: 'incident-report',
    name: 'Incident Report',
    description: 'Structured incident timeline, severity, impact, and remediation actions.',
    category: 'Operations',
    icon: 'AlertTriangle',
    gradient: 'from-red-500 to-orange-600',
    suggestedName: 'Incident Report',
    entities: [
      {
        name: 'incident',
        color: 'rose',
        data: {
          id: 'INC-2025-0842',
          title: 'API Latency Degradation',
          severity: 'P2 — Major',
          status: 'Resolved',
          detectedAt: '2025-06-18 14:22 UTC',
          resolvedAt: '2025-06-18 16:45 UTC',
          duration: '2h 23m',
          impact: 'Elevated response times for 12% of requests',
          rootCause: 'Database connection pool exhaustion under peak load',
          actions: [
            'Scaled connection pool from 50 to 120',
            'Added auto-scaling trigger at 80% utilization',
          ],
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #fef2f2; color: #7f1d1d; }
  .alert-bar { background: #dc2626; color: #fff; padding: 12px 20px; border-radius: 8px; font-size: 12px; font-weight: 600; margin-bottom: 16px; }
  h1 { font-size: 20px; color: #991b1b; }
  .id { font-size: 11px; color: #b91c1c; font-family: monospace; }
  .meta { display: flex; gap: 16px; margin: 16px 0; flex-wrap: wrap; }
  .tag { background: #fff; border: 1px solid #fecaca; padding: 8px 14px; border-radius: 6px; font-size: 12px; }
  .tag strong { display: block; font-size: 10px; text-transform: uppercase; color: #ef4444; margin-bottom: 2px; }
  .section { background: #fff; border: 1px solid #fecaca; border-radius: 10px; padding: 18px; margin-top: 16px; }
  .section h2 { font-size: 13px; color: #991b1b; margin-bottom: 8px; }
  .section p, .section li { font-size: 13px; color: #44403c; line-height: 1.6; }
  .section li { margin-left: 18px; margin-bottom: 4px; }
</style></head><body>
  <div class="alert-bar">INCIDENT REPORT · {{incident.severity}}</div>
  <h1>{{incident.title}}</h1>
  <p class="id">{{incident.id}} · Status: {{incident.status}}</p>
  <div class="meta">
    <div class="tag"><strong>Detected</strong>{{incident.detectedAt}}</div>
    <div class="tag"><strong>Resolved</strong>{{incident.resolvedAt}}</div>
    <div class="tag"><strong>Duration</strong>{{incident.duration}}</div>
  </div>
  <div class="section"><h2>Impact</h2><p>{{incident.impact}}</p></div>
  <div class="section"><h2>Root Cause</h2><p>{{incident.rootCause}}</p></div>
  <div class="section"><h2>Remediation Actions</h2>
    <ul><li>{{incident.actions.0}}</li><li>{{incident.actions.1}}</li></ul>
  </div>
</body></html>`,
  },
  {
    id: 'user-engagement',
    name: 'User Engagement',
    description: 'DAU/MAU, retention cohorts, feature adoption, and engagement score.',
    category: 'Analytics',
    icon: 'LineChart',
    gradient: 'from-teal-500 to-cyan-600',
    suggestedName: 'User Engagement Report',
    entities: [
      {
        name: 'engagement',
        color: 'teal',
        data: {
          title: 'User Engagement Report',
          period: 'May 2025',
          dau: '3,240',
          mau: '18,600',
          stickiness: '17.4%',
          retention: '68%',
          avgSessions: '4.2',
          topFeature: 'AI Assistant',
          engagementScore: 78,
        },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 28px; background: #f0fdfa; color: #134e4a; }
  h1 { font-size: 20px; color: #0d9488; }
  .period { font-size: 12px; color: #14b8a6; margin-bottom: 20px; }
  .score-ring { width: 100px; height: 100px; border-radius: 50%; background: conic-gradient(#14b8a6 {{engagement.engagementScore}}%, #ccfbf1 0); display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
  .score-inner { width: 76px; height: 76px; background: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 800; color: #0f766e; }
  .metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .m { background: #fff; border: 1px solid #99f6e4; border-radius: 10px; padding: 16px; }
  .m-label { font-size: 10px; text-transform: uppercase; color: #0d9488; font-weight: 600; }
  .m-val { font-size: 22px; font-weight: 800; color: #115e59; margin-top: 4px; }
  .feature { margin-top: 20px; background: #fff; padding: 14px; border-radius: 8px; font-size: 13px; border: 1px solid #99f6e4; }
</style></head><body>
  <h1>{{engagement.title}}</h1>
  <p class="period">{{engagement.period}}</p>
  <div class="score-ring"><div class="score-inner">{{engagement.engagementScore}}</div></div>
  <div class="metrics">
    <div class="m"><div class="m-label">DAU</div><div class="m-val">{{engagement.dau}}</div></div>
    <div class="m"><div class="m-label">MAU</div><div class="m-val">{{engagement.mau}}</div></div>
    <div class="m"><div class="m-label">Stickiness</div><div class="m-val">{{engagement.stickiness}}</div></div>
    <div class="m"><div class="m-label">Retention</div><div class="m-val">{{engagement.retention}}</div></div>
    <div class="m"><div class="m-label">Avg Sessions</div><div class="m-val">{{engagement.avgSessions}}</div></div>
  </div>
  <div class="feature">Top feature by usage: <strong>{{engagement.topFeature}}</strong></div>
</body></html>`,
  },
  {
    id: 'blank-canvas',
    name: 'Blank Canvas',
    description: 'Minimal starter with header, section placeholders, and entity binding examples.',
    category: 'Analytics',
    icon: 'FileText',
    gradient: 'from-neutral-500 to-neutral-700',
    suggestedName: 'Untitled Report',
    entities: [
      {
        name: 'report',
        color: 'indigo',
        data: { title: 'Your Report Title', subtitle: 'Add your description here', author: 'Analytics Hub', date: 'June 20, 2025' },
      },
    ],
    htmlTemplate: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: ${BASE_FONT}; padding: 32px; color: #334155; max-width: 720px; margin: 0 auto; }
  h1 { font-size: 24px; color: #1e293b; margin-bottom: 4px; }
  .sub { color: #64748b; font-size: 13px; margin-bottom: 32px; }
  .section { border: 2px dashed #cbd5e1; border-radius: 8px; padding: 24px; margin-bottom: 16px; min-height: 80px; }
  .section-label { font-size: 10px; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.1em; margin-bottom: 8px; }
  .footer { margin-top: 32px; font-size: 11px; color: #94a3b8; }
</style></head><body>
  <h1>{{report.title}}</h1>
  <p class="sub">{{report.subtitle}}</p>
  <div class="section"><div class="section-label">Section 1 — Add content</div><p>Replace with your HTML and {{report.author}} bindings.</p></div>
  <div class="section"><div class="section-label">Section 2 — Charts / Tables</div></div>
  <div class="footer">{{report.author}} · {{report.date}}</div>
</body></html>`,
  },
];

export const REPORT_TEMPLATES: ReportTemplate[] = [
  ...DASHBOARD_TEMPLATES,
  ...CORE_REPORT_TEMPLATES,
  ...EXTRA_REPORT_TEMPLATES,
];

export const REPORT_CATEGORY_META: Record<
  ReportTemplateCategory,
  { label: string; description: string; icon: string; gradient: string }
> = {
  Analytics: {
    label: 'Analytics',
    description: 'KPIs, engagement, marketing & product insights',
    icon: 'BarChart3',
    gradient: 'from-indigo-500 to-violet-600',
  },
  ChatBot: {
    label: 'ChatBot',
    description: 'Conversations, intents, handoffs & NLU metrics',
    icon: 'MessageSquare',
    gradient: 'from-cyan-500 to-blue-600',
  },
  Operations: {
    label: 'Operations',
    description: 'SLA, workflows, incidents & infrastructure',
    icon: 'Activity',
    gradient: 'from-emerald-500 to-teal-600',
  },
  Finance: {
    label: 'Finance',
    description: 'Revenue, budgets, cash flow & receivables',
    icon: 'TrendingUp',
    gradient: 'from-amber-500 to-orange-600',
  },
  Compliance: {
    label: 'Compliance',
    description: 'Audits, GDPR, access reviews & risk',
    icon: 'ClipboardList',
    gradient: 'from-slate-500 to-gray-700',
  },
  Sales: {
    label: 'Sales',
    description: 'Pipeline, quotas, regions & account health',
    icon: 'Users',
    gradient: 'from-rose-500 to-pink-600',
  },
};

export function getTemplatesByCategory(category: ReportTemplateCategory): ReportTemplate[] {
  return REPORT_TEMPLATES.filter((t) => t.category === category);
}

export function getCategoryTemplateCounts(): Record<ReportTemplateCategory, number> {
  return REPORT_TEMPLATE_CATEGORIES.reduce(
    (acc, cat) => {
      acc[cat] = REPORT_TEMPLATES.filter((t) => t.category === cat).length;
      return acc;
    },
    {} as Record<ReportTemplateCategory, number>
  );
}

export function groupTemplatesByCategory(
  templates: ReportTemplate[]
): Record<ReportTemplateCategory, ReportTemplate[]> {
  return REPORT_TEMPLATE_CATEGORIES.reduce(
    (acc, cat) => {
      acc[cat] = templates.filter((t) => t.category === cat);
      return acc;
    },
    {} as Record<ReportTemplateCategory, ReportTemplate[]>
  );
}

export function cloneTemplateEntities(template: ReportTemplate): ReportEntity[] {
  return template.entities.map((e, i) => ({
    ...e,
    id: `tpl-${template.id}-${i}-${Date.now()}`,
  }));
}
